<template>
    <BaseModal
        class="rename-bill-modal"
        :closable="true"
        :hasHeader="false"
        :hasFooter="false"
        ref="baseModalComponent"
    >
        <template v-slot:body>
            <div class="rename-bill-form form-group row">
                <div class="col-md">
                    <label for="bill-name" class="col-form-label"
                        >{{ translations.renameBillModal.billNameLabel }}:</label
                    >
                    <div class="input-group">
                        <input
                            type="text"
                            id="bill-name"
                            name="bill-name"
                            class="form-control"
                            :placeholder="translations.renameBillModal.billNamePlaceholder"
                            autocomplete="off"
                            v-model="billName"
                            @keypress="onKeyPress"
                        />
                        <button
                            type="button"
                            class="save-btn btn btn-success"
                            :disabled="isSubmitDisabled"
                            @click="saveChanges"
                        >
                            {{ trans.general.save }}
                        </button>
                    </div>
                </div>
            </div>
        </template>
    </BaseModal>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { Factory } from "@/data/dao/Factory";
    import { OperatorDao } from "@/data/dao/service/OperatorDao";
    import { useBillStore } from "@/data/stores/bill";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { Logging } from "@/data/util/Logging";
    import { isNullOrWhitespace } from "@/data/util/String";
    import { trans } from "@/operator/localization/Trans";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed, ref, watch } from "vue";

    import BaseModal from "@/components/dialog/BaseModal.vue";

    type OriginalBillData = {
        id: string;
        billName: string | undefined;
        allocationHash: string;
    };

    const operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);
    const bill = useBillStore();
    const allocation = useTableAllocationStore();

    // define html refs
    const baseModalComponent = ref<InstanceType<typeof BaseModal> | null>(null);
    const isSaving = ref(false);

    const originalBill = ref<OriginalBillData>();
    const billName = ref<string | undefined>();

    const isBillNameChanged = computed(() => trim(billName.value) != trim(originalBill.value!.billName));
    const isSubmitDisabled = computed(
        () => isSaving.value || isNullOrWhitespace(billName.value) || !isBillNameChanged.value
    );

    async function onKeyPress(e: KeyboardEvent) {
        if (!isSubmitDisabled.value && e.key === "Enter") {
            saveChanges();
        }
    }

    async function saveChanges() {
        isSaving.value = true;

        try {
            await operatorDao.updateBillName(bill.currentBill!.id, {
                name: trim(billName.value),
            });
            bill.updateBillName(bill.currentBill!.id, billName.value!);
            close();
        } catch (ex) {
            logError(ex);
            Popup.show(
                {
                    containerClass: "rename-bill-error-popup",
                    title: trans.general.error,
                    message: trans.general.operationFailed,
                },
                $("body")
            );
        } finally {
            isSaving.value = false;
        }
    }

    function trim(value?: string) {
        return (value ?? "").trim();
    }

    watch(
        () => allocation.hash,
        () => {
            if (baseModalComponent?.value?.isVisible != true) return;

            if (allocation.hash != originalBill.value!.allocationHash) {
                // if allocation has changed - close this modal
                Logging.LogInfo(
                    `Closing RenameBillModal because allocation has changed - previous ${originalBill.value!.allocationHash}, current ${allocation.hash}`
                );
                close();
            }
        }
    );

    // define exposed
    function show() {
        originalBill.value = {
            id: bill.currentBill!.id,
            billName: bill.currentBill!.customName,
            allocationHash: allocation.hash,
        };
        billName.value = bill.currentBill!.customName;

        baseModalComponent.value?.show();
    }

    function close() {
        baseModalComponent.value?.close();
    }

    defineExpose({
        show,
        close,
    });
</script>

<style lang="scss">
    .modal.rename-bill-modal {
        .modal-body {
            padding: 1.5em 2em;
        }

        .save-btn {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
        }

        .btn[disabled] {
            cursor: default;
        }
    }

    .rename-bill-error-popup {
        z-index: 1510;
    }
</style>
